import React, { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { MessageSquare, X } from "lucide-react";
import { RootState, AppDispatch } from "configureStore";
import {
  setLauncherExpanded,
  closeMiniChat,
  setMiniChatActive,
} from "./chat.slice";
import UserAvatar from "components/user-avatar/user-avatar";
import { MiniChatState } from "types/Chat";
import { useTranslation } from "react-i18next";

/** Max avatar slots in the vertical strip (including the +N overflow chip when needed). */
const MAX_LAUNCHER_SLOTS = 5;

export const MiniChatLauncher: React.FC = () => {
  const { t } = useTranslation();
  const dispatch: AppDispatch = useDispatch();
  const { activeMiniChats, isLauncherExpanded, unreadByCompany } = useSelector(
    (state: RootState) => state.chatReducer
  );
  const companies = useSelector(
    (state: RootState) => state.companySelectReducer?.companiesCollectionRaw || []
  ) as Array<{ id: string; name: string; legalName?: string }>;

  const [overflowOpen, setOverflowOpen] = useState(false);
  const overflowRootRef = useRef<HTMLDivElement | null>(null);

  const companyLabel = (companyId: string) => {
    const u = unreadByCompany[companyId]?.name;
    if (u) return u;
    const c = companies.find((x) => String(x.id) === String(companyId));
    return c?.legalName || c?.name || "";
  };

  const sortedChats = useMemo(
    () => [...activeMiniChats].sort((a, b) => b.lastActiveAt - a.lastActiveAt),
    [activeMiniChats]
  );

  const { stripChats, overflowChats } = useMemo(() => {
    if (sortedChats.length <= MAX_LAUNCHER_SLOTS) {
      return { stripChats: sortedChats, overflowChats: [] as MiniChatState[] };
    }
    return {
      stripChats: sortedChats.slice(0, MAX_LAUNCHER_SLOTS - 1),
      overflowChats: sortedChats.slice(MAX_LAUNCHER_SLOTS - 1),
    };
  }, [sortedChats]);

  useEffect(() => {
    if (!overflowOpen) return;
    const onDocMouseDown = (e: MouseEvent) => {
      const el = overflowRootRef.current;
      if (el && !el.contains(e.target as Node)) {
        setOverflowOpen(false);
      }
    };
    document.addEventListener("mousedown", onDocMouseDown);
    return () => document.removeEventListener("mousedown", onDocMouseDown);
  }, [overflowOpen]);

  if (activeMiniChats.length === 0) {
    return null;
  }

  return (
    <div className="flex flex-col items-center gap-3 pointer-events-auto">
      {isLauncherExpanded && (
        <div className="flex flex-col-reverse items-center gap-3 mb-2 animate-in slide-in-from-bottom-4 duration-300">
          {stripChats.map((chat) => {
            const companyName = companyLabel(chat.companyId);
            const isCompanyChat = chat.conversationType === "company";
            const displayTitle = isCompanyChat ? t("company_chat") : chat.title;
            const avatarName = isCompanyChat ? companyName || displayTitle : chat.title;

            return (
              <div key={chat.conversationId} className="relative group">
                <div className="absolute right-full mr-3 top-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap bg-gray-800 text-white text-[10px] px-2 py-1 rounded shadow-lg border border-gray-700 z-10">
                  <div className="font-bold">{displayTitle}</div>
                  {companyName && (
                    <div className="text-gray-400 text-[9px]">{companyName}</div>
                  )}
                </div>

                <button
                  type="button"
                  onClick={() => {
                    dispatch(setMiniChatActive(chat.conversationId));
                  }}
                  className={`w-12 h-12 rounded-full border-2 transition-all duration-200 hover:scale-110 shadow-xl overflow-hidden relative ${
                    !chat.isMinimized
                      ? "border-blue-500 scale-105"
                      : "border-gray-700 hover:border-gray-500"
                  }`}
                >
                  <UserAvatar
                    companyId={chat.companyId}
                    user={{
                      id: "",
                      name: avatarName || "",
                      avatarFileId: chat.avatarFileId,
                    }}
                    size={48}
                  />
                </button>

                {/* Always shown on a touch screen (no hover there). There it sits just outside
                    the avatar's rim and its small extra hit area stays clear of the avatar,
                    so tapping the avatar's upper right still opens the chat. */}
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    dispatch(closeMiniChat(chat.conversationId));
                  }}
                  className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 [@media(hover:none)]:opacity-100 [@media(hover:none)]:-top-2 [@media(hover:none)]:-right-2 [@media(hover:none)]:before:content-[''] [@media(hover:none)]:before:absolute [@media(hover:none)]:before:-inset-1.5 transition-opacity shadow-md border border-gray-900 z-10"
                  aria-label="Close chat"
                >
                  <X size={10} />
                </button>
              </div>
            );
          })}

          {overflowChats.length > 0 && (
            <div className="relative group" ref={overflowRootRef}>
              {overflowOpen && (
                <div
                  className="absolute right-full mr-3 top-1/2 z-[500] min-w-[220px] max-w-[280px] -translate-y-1/2 rounded-lg border border-gray-200 bg-white py-1 shadow-xl"
                >
                  <div className="absolute -right-1.5 top-1/2 h-3 w-3 -translate-y-1/2 rotate-45 border-r border-t border-gray-200 bg-white" />
                  {overflowChats.map((chat) => {
                    const co = companyLabel(chat.companyId);
                    const isCompanyChat = chat.conversationType === "company";
                    const displayTitle = isCompanyChat ? t("company_chat") : chat.title;

                    return (
                      <div
                        key={chat.conversationId}
                        className="flex items-center gap-2 px-3 py-2 hover:bg-gray-50"
                      >
                        <button
                          type="button"
                          className="min-w-0 flex-1 text-left"
                          onClick={() => {
                            dispatch(setMiniChatActive(chat.conversationId));
                            setOverflowOpen(false);
                          }}
                        >
                          <div className="truncate text-sm text-gray-900">
                            {displayTitle || "—"}
                          </div>
                          {co && (
                            <div className="truncate text-[11px] text-gray-500">{co}</div>
                          )}
                        </button>
                        <button
                          type="button"
                          className="shrink-0 rounded p-1 max-md:p-3 text-gray-400 hover:bg-gray-100 hover:text-gray-700"
                          onClick={() => {
                            dispatch(closeMiniChat(chat.conversationId));
                            setOverflowOpen(false);
                          }}
                          aria-label="Close chat"
                        >
                          <X size={14} />
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}

              <button
                type="button"
                onClick={() => setOverflowOpen((o) => !o)}
                className="relative flex h-12 w-12 items-center justify-center rounded-full border-2 border-gray-700 bg-gray-800 text-sm font-bold text-white shadow-xl transition-all duration-200 hover:scale-110 hover:border-gray-500"
                aria-label={`${overflowChats.length} more conversations`}
              >
                <span className="relative z-[1]">+{overflowChats.length}</span>
                {overflowChats[0] && (() => {
                  const firstOverflow = overflowChats[0];
                  const coName = companyLabel(firstOverflow.companyId);
                  const isComp = firstOverflow.conversationType === "company";
                  const fallbackName = isComp ? coName || t("company_chat") : firstOverflow.title;
                  return (
                    <span className="absolute inset-0 overflow-hidden rounded-full opacity-40">
                      <UserAvatar
                        companyId={firstOverflow.companyId}
                        user={{
                          id: "",
                          name: fallbackName || "",
                          avatarFileId: firstOverflow.avatarFileId,
                        }}
                        size={48}
                      />
                    </span>
                  );
                })()}
              </button>
            </div>
          )}
        </div>
      )}

      <button
        type="button"
        onClick={() => dispatch(setLauncherExpanded(!isLauncherExpanded))}
        className={`relative flex h-14 w-14 items-center justify-center rounded-full shadow-2xl transition-all duration-300 hover:scale-105 ${
          isLauncherExpanded
            ? "bg-blue-600 text-white rotate-0"
            : "bg-white text-blue-600 hover:bg-gray-50"
        }`}
      >
        <MessageSquare size={28} className={isLauncherExpanded ? "scale-90" : ""} />

        {!isLauncherExpanded && activeMiniChats.length > 0 && (
          <div className="absolute -top-1 -right-1 flex h-6 w-6 items-center justify-center rounded-full border-2 border-white bg-red-500 text-[10px] font-bold text-white">
            {activeMiniChats.length > 99 ? "99+" : activeMiniChats.length}
          </div>
        )}
      </button>
    </div>
  );
};
